import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AppLayout } from '../components/layout';
import { Card, Button, Input } from '../components/ui';
import useRolesStore from '../stores/rolesStore';

const roleSchema = z.object({
	name: z.string().min(1, 'Role name is required'),
	duration: z.string().min(1, 'Duration is required'),
});

type RoleFormData = z.infer<typeof roleSchema>;

const EditRole = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { roles, updateRole } = useRolesStore();

	const role = roles.find(r => r.id === id);

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<RoleFormData>({
		resolver: zodResolver(roleSchema),
		defaultValues: role || {},
	});

	if (!role) {
		return (
			<AppLayout>
				<Card>
					<div className="text-center py-12">
						<h2 className="text-2xl font-bold text-gray-900 mb-4">Role Not Found</h2>
						<Button variant="primary" onClick={() => navigate('/roles')}>
							Back to Roles
						</Button>
					</div>
				</Card>
			</AppLayout>
		);
	}

	const onSubmit = async (data: RoleFormData) => {
		const updatePromise = new Promise(resolve => {
			setTimeout(() => {
				updateRole(id!, {
					id: id!,
					...data,
				});
				resolve(true);
			}, 1000);
		});

		toast.promise(updatePromise, {
			loading: 'Updating role...',
			success: 'Role updated successfully!',
			error: 'Failed to update role',
		});

		try {
			await updatePromise;
			navigate('/roles');
		} catch (error) {
			console.error('Error updating role:', error);
		}
	};

	return (
		<AppLayout showTopbar={false}>
			{/* Back Link */}
			<div
				className="flex items-center gap-2 text-blue-600 cursor-pointer hover:underline mb-4 text-sm font-medium"
				onClick={() => navigate('/roles')}
			>
				<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
					<path
						fillRule="evenodd"
						d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
					/>
				</svg>
				Back to Roles
			</div>

			<Card className="max-w-3xl">
				<div className="mb-6">
					<h2 className="text-2xl font-bold text-gray-900 mb-2">Edit Role</h2>
					<div className="flex items-center gap-2 text-sm text-gray-600">
						<span className="text-blue-600 cursor-pointer hover:underline" onClick={() => navigate('/dashboard')}>
							Dashboard
						</span>
						<span>›</span>
						<span className="text-blue-600 cursor-pointer hover:underline" onClick={() => navigate('/settings')}>
							Settings
						</span>
						<span>›</span>
						<span className="text-blue-600 cursor-pointer hover:underline" onClick={() => navigate('/roles')}>
							Roles
						</span>
						<span>›</span>
						<span>Edit Role</span>
					</div>
				</div>

				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="space-y-5">
						{/* Role Name */}
						<Input
							label="Role Name"
							placeholder="e.g., Stylist, Braider, Manager"
							error={errors.name?.message}
							required
							disabled={isSubmitting}
							{...register('name')}
							helperText="Enter the name of the role"
						/>

						{/* Duration */}
						<Input
							label="Duration"
							placeholder="e.g., 2 hours, 3.5 hours, Full Day"
							error={errors.duration?.message}
							required
							disabled={isSubmitting}
							{...register('duration')}
							helperText='Specify the typical duration for this role (e.g., "2 hours", "3.5 hours", "Full Day")'
						/>
					</div>

					{/* Action Buttons */}
					<div className="flex gap-3 mt-6">
						<Button type="submit" variant="primary" isLoading={isSubmitting}>
							{isSubmitting ? 'Updating Role...' : 'Update Role'}
						</Button>
						<Button type="button" variant="secondary" onClick={() => navigate('/roles')} disabled={isSubmitting}>
							Cancel
						</Button>
					</div>
				</form>
			</Card>

			<div className="mt-6 text-sm text-gray-500">© Beauty Business Portal</div>
		</AppLayout>
	);
};

export default EditRole;
